import {Navigate, useNavigate} from 'react-router-dom';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {getPlan, STEP_LABELS, stepProduct} from '@client/pages/public/enroll/enrollModel';
import {TierFilter} from '@client/pages/public/enroll/components/TierFilter';
import {PlanCard} from '@client/pages/public/enroll/components/PlanCard';
import {StepVideo} from '@client/pages/public/enroll/components/StepVideo';
import {BenefitsTable} from '@client/pages/public/enroll/components/BenefitsTable';
import {DeclinedNotice} from '@client/pages/public/enroll/components/DeclinedNotice';
import {useEqualizeHeights} from '@client/pages/public/enroll/hooks/useEqualizeHeights';
import type {BenefitRow, GroupConfig, ProductCategory} from '@client/pages/public/enroll/types';

// Above this many plans the cards get too narrow for a per-card "Continue to X" label.
const COMPACT_GRID_THRESHOLD = 3;

function gridClass(count: number): string {
  if (count <= 1) return 'max-w-md';
  if (count === 2) return 'sm:grid-cols-2';
  if (count === COMPACT_GRID_THRESHOLD) return 'sm:grid-cols-2 lg:grid-cols-3';
  // 4-up collides with the 320px cart rail in the 1024-1280 band (cards squeeze to ~150px), so hold
  // at 2-up until xl. Left the 3-up case at lg — it's roomier and no live group uses it (revisit if one does).
  return 'sm:grid-cols-2 xl:grid-cols-4';
}

interface PlanGridProps {
  group: GroupConfig;
  category: ProductCategory;
  planIds: string[];
  benefitRows?: BenefitRow[];
  benefitsTitle?: string;
  selectedPlanId?: string;
  continueLabel?: string;
  onSelectPlan: (category: ProductCategory, planId: string) => void;
  onContinue?: () => void;
  onDeselect?: () => void;
}

function PlanGrid({
  group,
  category,
  planIds,
  benefitRows,
  benefitsTitle,
  selectedPlanId,
  continueLabel,
  onSelectPlan,
  onContinue,
  onDeselect,
}: PlanGridProps) {
  const {getTier} = useEnrollSteps();
  const tier = getTier(category);
  const plans = planIds
    .map((id) => getPlan(group, id))
    .filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));
  const showBadge = !group.products.find((p) => p.category === category)?.hideLowestCostBadge;
  const cheapestId =
    showBadge && plans.length > 1
      ? plans.reduce((lowest, plan) => (plan.rates[tier] < lowest.rates[tier] ? plan : lowest)).id
      : undefined;
  const gridRef = useEqualizeHeights<HTMLDivElement>('[data-equalize="highlights"]', [
    planIds.join(','),
  ]);

  return (
    <div className='space-y-6'>
      <div ref={gridRef} className={cn('grid grid-cols-1 gap-5', gridClass(plans.length))}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            tier={tier}
            selected={selectedPlanId === plan.id}
            onSelect={() => onSelectPlan(category, plan.id)}
            badge={plan.id === cheapestId ? 'Lowest cost' : undefined}
            accentColor={plan.accentColor}
            continueLabel={plans.length > COMPACT_GRID_THRESHOLD ? undefined : continueLabel}
            onContinue={onContinue}
            onDeselect={onDeselect}
          />
        ))}
      </div>
      {benefitRows && benefitRows.length > 0 && (
        <div className='space-y-3'>
          <h3 className='text-base font-semibold text-neutral-900'>{benefitsTitle}</h3>
          <BenefitsTable
            group={group}
            planIds={planIds}
            rows={benefitRows}
            selectedPlanId={selectedPlanId}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Step screen for a single product category (medical/dental/vision). The `category` prop
 * selects which one; it renders the tier filter, a decline/skip notice, the gating plan grid,
 * and any additive opt-in products that render inline beneath this category (`inlineWith`),
 * e.g. AXIS limited medical under Medical.
 */
export function ProductStep({category: step}: {category: 'medical' | 'dental' | 'vision'}) {
  const {group, selections, selectPlan, clearSelection, steps, currentIndex} = useEnrollSteps();
  const navigate = useNavigate();
  // On a site without regular medical, limited medical is the medical step's main product.
  const product = stepProduct(group, step);

  if (!product) return <Navigate to={`/${group.slug}/${steps[0]}`} replace />;

  const category = product.category;
  const selectedPlanId = selections[category]?.planId;
  const declined = selections[category]?.declined;

  const nextStep =
    currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : undefined;
  const continueLabel = nextStep ? STEP_LABELS[nextStep] : undefined;
  const goToNext = nextStep ? () => navigate(`/${group.slug}/${nextStep}`) : undefined;

  const inlineProducts = group.products.filter((p) => p.inlineWith === step && p !== product);

  return (
    <div className={cn('space-y-8', POSTHOG_UNMASK_CLASS)}>
      {product.videoUrl && (
        <div className='mx-auto' style={{width: `${product.videoWidthPercent ?? 100}%`}}>
          <StepVideo url={product.videoUrl} />
        </div>
      )}

      {product.tierSelectable !== false && <TierFilter category={category} />}

      {declined && <DeclinedNotice product={product} onUndo={() => clearSelection(category)} />}

      <PlanGrid
        group={group}
        category={category}
        planIds={product.planIds}
        benefitRows={product.benefitRows}
        benefitsTitle={
          product.benefitsTableTitle ?? (category === 'medical' ? 'Compare benefits' : "What's covered")
        }
        selectedPlanId={selectedPlanId}
        continueLabel={continueLabel}
        onSelectPlan={selectPlan}
        onContinue={goToNext}
      />

      {inlineProducts.map((inline) => (
        <section key={inline.category} className='space-y-4 border-t border-neutral-200 pt-8'>
          <div className='space-y-1'>
            {inline.sectionLabel && (
              <p className='text-xs font-bold uppercase tracking-wide text-[var(--enroll-accent)]'>
                {inline.sectionLabel}
              </p>
            )}
            <h2 className='text-2xl font-bold text-[var(--enroll-ink)]'>{inline.label}</h2>
            <p className='max-w-2xl text-neutral-600'>{inline.description}</p>
          </div>
          {inline.tierSelectable !== false && <TierFilter category={inline.category} />}
          <PlanGrid
            group={group}
            category={inline.category}
            planIds={inline.planIds}
            benefitRows={inline.benefitRows}
            benefitsTitle={inline.benefitsTableTitle ?? "What's covered"}
            selectedPlanId={selections[inline.category]?.planId}
            onSelectPlan={selectPlan}
            onDeselect={() => clearSelection(inline.category)}
          />
        </section>
      ))}
    </div>
  );
}
